import { useCallback } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import {
	type IntentClassificationData,
	isNavContentIntent,
	isNavigationalIntent,
} from '@atlassian/search-ai';

interface SmartAnswersIntentStore {
	workspaceId: string | null;
	intentClassificationData: IntentClassificationData | null | undefined;
}

const NAV_BOOST_SCORE_THRESHOLD = 0.5;

const initialState: SmartAnswersIntentStore = {
	workspaceId: null,
	intentClassificationData: null,
};

// Singleton state shared across all components
const sharedState: { current: SmartAnswersIntentStore } = {
	current: { ...initialState },
};

/**
 * React hook for managing smart answers intent store
 * Provides state management for smart answers high confidence result cards
 * Uses a shared singleton state to ensure all components see the same results
 */
export const useSmartAnswersIntentStore = () => {
	const state = sharedState;

	const setWorkspaceId = useCallback(
		(workspaceId: string): void => {
			state.current.workspaceId = workspaceId;
		},
		[state],
	);

	const getWorkspaceId = useCallback((): string | null => {
		return state.current.workspaceId;
	}, [state]);

	const setQueryIntentClassificationData = useCallback(
		(intentClassificationData: IntentClassificationData): void => {
			state.current.intentClassificationData = intentClassificationData;
		},
		[state],
	);

	const clearStore = useCallback((): void => {
		state.current = { ...initialState };
	}, [state]);

	const isSAINNavigationalExperienceEligible = useCallback(
		({
			position,
			url,
			navBoostScore,
		}: {
			position: number;
			url: string;
			navBoostScore: number | null | undefined;
		}): boolean => {
			if (position === 0 && fg('kd_enable_sain_hero_nav')) {
				const queryIntentClassificationData = state.current.intentClassificationData;

				// use the URL check for nav content intent
				if (queryIntentClassificationData && isNavContentIntent(queryIntentClassificationData)) {
					const navContentUrl = queryIntentClassificationData.url;
					if (navContentUrl === url) {
						return true;
					}
				}

				// use the nav boost score check for navigational intent or nav content intent if we missed the document
				// because the URL returned is not for the first result
				if (
					queryIntentClassificationData &&
					(isNavigationalIntent(queryIntentClassificationData) ||
						isNavContentIntent(queryIntentClassificationData))
				) {
					if (navBoostScore && navBoostScore > NAV_BOOST_SCORE_THRESHOLD) {
						return true;
					}
				}
			}

			return false;
		},
		[state],
	);

	return {
		setWorkspaceId,
		getWorkspaceId,
		setQueryIntentClassificationData,
		isSAINNavigationalExperienceEligible,
		clearStore,
	};
};
